import { useState, useMemo } from 'react'
import ReactECharts from 'echarts-for-react'

interface TrafficTrendChartProps {
  data: Record<string, any>[]
  previousData?: Record<string, any>[] | null
}

const TABS = [
  { key: 'sessions', label: 'Visites', color: '#6366F1', gradient: 'rgba(99,102,241,0.15)' },
  { key: 'totalUsers', label: 'Uniques', color: '#10B981', gradient: 'rgba(16,185,129,0.15)' },
  { key: 'screenPageViewsPerSession', label: 'Pages/Visite', color: '#F59E0B', gradient: 'rgba(245,158,11,0.15)' },
  { key: 'averageSessionDuration', label: 'Dur\u00e9e moy.', color: '#8B5CF6', gradient: 'rgba(139,92,246,0.15)' },
  { key: 'bounceRate', label: 'Rebond', color: '#EF4444', gradient: 'rgba(239,68,68,0.15)' },
] as const

const dateFormatter = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' })

const fmtDuration = (s: number) => {
  const m = Math.floor(s / 60)
  const sec = Math.floor(s % 60)
  return m > 0 ? `${m}m${sec.toString().padStart(2, '0')}s` : `${sec}s`
}

export function TrafficTrendChart({ data, previousData }: TrafficTrendChartProps) {
  const [activeTab, setActiveTab] = useState<string>(TABS[0].key)

  const activeConfig = TABS.find((t) => t.key === activeTab) ?? TABS[0]

  const option = useMemo(() => {
    const isBounce = activeTab === 'bounceRate'
    const isDuration = activeTab === 'averageSessionDuration'

    const fmtValue = (v: number) => {
      if (isDuration) return fmtDuration(v)
      if (isBounce) return `${Math.round(v * 10) / 10}%`
      return v.toLocaleString('fr-FR')
    }

    const dates = data.map((d) => {
      const raw = d.date ?? ''
      const parsed = new Date(raw)
      return isNaN(parsed.getTime()) ? String(raw) : dateFormatter.format(parsed)
    })

    const values = data.map((d) => {
      const v = d[activeTab]
      return v == null ? 0 : Number(v)
    })

    // Comparison overlay — align by index (day 1 vs day 1)
    const hasPrevious = previousData && previousData.length > 0
    const prevValues = hasPrevious
      ? previousData!.map((d) => {
          const v = d[activeTab]
          return v == null ? 0 : Number(v)
        })
      : []

    const series: any[] = [
      {
        type: 'line',
        name: 'Actuel',
        smooth: 0.4,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 2.5, color: activeConfig.color },
        itemStyle: { color: activeConfig.color },
        areaStyle: {
          color: {
            type: 'linear' as const,
            x: 0, y: 0, x2: 0, y2: 1,
            colorStops: [
              { offset: 0, color: activeConfig.gradient },
              { offset: 1, color: 'rgba(255,255,255,0)' },
            ],
          },
        },
        data: values,
      },
    ]

    if (hasPrevious) {
      series.push({
        type: 'line',
        name: 'Pr\u00e9c\u00e9dent',
        smooth: 0.4,
        symbol: 'none',
        lineStyle: {
          width: 1.5,
          color: activeConfig.color,
          opacity: 0.4,
          type: 'dashed' as const,
        },
        itemStyle: { color: activeConfig.color, opacity: 0.4 },
        data: prevValues,
      })
    }

    return {
      grid: { left: 56, right: 16, top: 16, bottom: hasPrevious ? 40 : 32 },
      legend: hasPrevious ? {
        bottom: 0,
        textStyle: { color: '#8B8FA7', fontSize: 11 },
        itemWidth: 20,
        itemHeight: 2,
      } : undefined,
      xAxis: {
        type: 'category' as const,
        data: dates,
        axisLine: { lineStyle: { color: '#E2E4ED' } },
        axisLabel: { color: '#8B8FA7', fontSize: 10 },
        axisTick: { show: false },
      },
      yAxis: {
        type: 'value' as const,
        splitLine: { lineStyle: { type: 'dashed' as const, color: '#F4F5FA' } },
        axisLabel: {
          color: '#8B8FA7',
          fontSize: 10,
          formatter: (v: number) => {
            if (isDuration) return fmtDuration(v)
            if (isBounce) return `${v}%`
            if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k`
            return String(v)
          },
        },
      },
      tooltip: {
        trigger: 'axis' as const,
        backgroundColor: '#1B2559',
        textStyle: { color: '#fff' },
        borderColor: '#1B2559',
        borderRadius: 8,
        formatter: (params: any) => {
          const items = Array.isArray(params) ? params : [params]
          let html = `<div style="font-size:12px"><strong>${items[0].name}</strong>`
          for (const p of items) {
            const marker = p.seriesIndex === 0
              ? `<span style="display:inline-block;width:8px;height:2px;background:${activeConfig.color};margin-right:6px;vertical-align:middle"></span>`
              : `<span style="display:inline-block;width:8px;height:2px;background:${activeConfig.color};opacity:0.4;margin-right:6px;vertical-align:middle;border-top:1px dashed ${activeConfig.color}"></span>`
            html += `<br/>${marker}${fmtValue(p.value)}`
          }
          html += '</div>'
          return html
        },
      },
      series,
    }
  }, [data, previousData, activeTab, activeConfig])

  const isEmpty = !data || data.length === 0

  return (
    <div className="bg-white border border-[#E2E4ED] rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-[#1B2559]">Tendance de trafic</h3>
        <div className="flex gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${
                activeTab === tab.key
                  ? 'text-white'
                  : 'text-[#8B8FA7] hover:text-[#555A73] hover:bg-[#F4F5FA]'
              }`}
              style={activeTab === tab.key ? { backgroundColor: tab.color } : undefined}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {isEmpty ? (
        <div className="flex items-center justify-center h-[280px] text-sm text-[#8B8FA7]">
          Aucune donn&eacute;e disponible
        </div>
      ) : (
        <ReactECharts option={option} style={{ height: 280 }} notMerge />
      )}
    </div>
  )
}
